import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { setMuted, setMidiRange, audioSupported } from "../lib/keyboardPlan";
import {
  TRACKS,
  trackPath,
  unitTeach,
  lessonTeach,
  buildLesson,
  conceptsDue,
  lessonConceptPairs,
  buildCapstone,
  buildTrackCapstone,
  buildReviewLesson,
  buildKeyDrill,
  buildDailyChallenge,
  dailyDoneToday,
  dailyGoalProgress,
  nextStreak,
  lessonXp,
  applyResults,
  recordHistory,
  unlockedConcepts,
  allConcepts,
  unlockedKeys,
  earReadyKeys,
  unlockedSongs,
  normalizeSettings,
  DEFAULT_SETTINGS,
  grooveIndex,
} from "../lib/numberSenseData";
import { Keys, AudioSettingsContext } from "../components/numberSenseUi";
import { useMidiPresence } from "../components/numberSense/useMidi";
import HomeScreen from "../components/numberSense/HomeScreen";
import StatsScreen from "../components/numberSense/StatsScreen";
import GamesHub from "../components/numberSense/GamesHub";
import GameScreen from "../components/numberSense/GameScreen";
import DoneScreen from "../components/numberSense/DoneScreen";
import GrooveChoice from "../components/numberSense/GrooveChoice";
import LessonScreen from "../components/numberSense/LessonScreen";
import SyncScreen from "../components/numberSense/SyncScreen";
import SettingsScreen from "../components/numberSense/SettingsScreen";
import TeachScreen from "../components/numberSense/TeachScreen";
import LearnScreen from "../components/numberSense/LearnScreen";
import JamScreen from "../components/numberSense/JamScreen";
import PatternTrainerScreen from "../components/numberSense/PatternTrainerScreen";
import MenuScreen from "../components/numberSense/MenuScreen";
import ChartScreen from "../components/numberSense/ChartScreen";
import DrillScreen from "../components/numberSense/DrillScreen";
import FlowScreen from "../components/numberSense/FlowScreen";
import "../styles/numbersense.css";

/* ============================================================
   Number Sense — a gamified Nashville Number System trainer.
   The musical companion to /keyboard: learn to think in numbers
   (I–IV–V–vi…) across keys and drill it like a game.

   This file is the orchestrator: it owns persisted progress
   (per-device, in Firestore at numberSense/{userId} with a
   localStorage fallback) and the transient screen/lesson state,
   then routes to the screen components in components/numberSense/.
   ============================================================ */

const USER_KEY = "numberSenseUserId";
const LOCAL_PROGRESS_KEY = "numberSenseProgress";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getUserId = () => {
  let id = localStorage.getItem(USER_KEY);
  if (!id) {
    id = newId();
    localStorage.setItem(USER_KEY, id);
  }
  return id;
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

// Can this browser drive the groove? Timed play-the-changes needs audio (any
// input — MIDI, glass, or the typing keyboard — can play the chords).
const CAN_PLAY = audioSupported();

const NumberSense = () => {
  // ---- persisted progress (synced per-user) --------------------------------
  const [userId, setUserId] = useState(getUserId);
  const [progress, setProgress] = useState({
    xp: 0,
    streak: 0,
    completed: {},
    mastery: {},
    history: {},
    name: "",
    lastDate: "",
    dailyDate: "",
    settings: DEFAULT_SETTINGS,
    track: "major",
    unlockAll: false,
  });
  const [loaded, setLoaded] = useState(false);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const docRef = useMemo(
    () => (db ? doc(db, "numberSense", userId) : null),
    [userId]
  );

  useEffect(() => {
    if (!docRef) {
      try {
        const saved = localStorage.getItem(LOCAL_PROGRESS_KEY);
        if (saved) {
          const d = JSON.parse(saved);
          setProgress((p) => ({ ...p, ...d, settings: normalizeSettings(d.settings) }));
        }
      } catch {
        /* ignore */
      }
      setLoaded(true);
      return undefined;
    }
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setProgress({
          xp: d.xp || 0,
          streak: d.streak || 0,
          completed: d.completed || {},
          mastery: d.mastery || {},
          history: d.history || {},
          name: d.name || "",
          lastDate: d.lastDate || "",
          dailyDate: d.dailyDate || "",
          settings: normalizeSettings(d.settings),
          track: d.track || "major",
          unlockAll: Boolean(d.unlockAll),
        });
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return unsub;
  }, [docRef]);

  const persist = useCallback(
    (patch) => {
      const next = { ...progressRef.current, ...patch };
      setProgress(next);
      try {
        localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private-mode */
      }
      if (docRef) {
        setDoc(docRef, { ...patch, updatedAt: Date.now() }, { merge: true }).catch(
          () => {}
        );
      }
    },
    [docRef]
  );

  const awardXp = useCallback(
    (n) => {
      if (!n) return;
      persist({
        xp: (progressRef.current.xp || 0) + n,
        history: recordHistory(progressRef.current.history || {}, todayKey(), {
          xp: n,
        }),
      });
    },
    [persist]
  );

  // Fold a batch of { concept, correct } results (from a finished game) into the
  // per-concept mastery map. Games batch and report once on game-over so we
  // don't write to Firestore on every answer.
  const recordResults = useCallback(
    (results) => {
      if (!results || !results.length) return;
      const mastery = applyResults(
        progressRef.current.mastery || {},
        results,
        todayKey()
      );
      persist({ mastery });
    },
    [persist]
  );

  // Audio settings: keep the engine's mute flag in sync, and persist edits.
  const settings = progress.settings || DEFAULT_SETTINGS;
  useEffect(() => {
    setMuted(settings.muted);
  }, [settings.muted]);

  // …and the MIDI range calibration, so every hook maps incoming notes onto
  // the on-screen piano's window.
  useEffect(() => {
    setMidiRange(settings.midiLow, settings.midiHigh);
  }, [settings.midiLow, settings.midiHigh]);

  const updateSettings = useCallback(
    (patch) =>
      persist({
        settings: normalizeSettings({
          ...(progressRef.current.settings || DEFAULT_SETTINGS),
          ...patch,
        }),
      }),
    [persist]
  );

  // Re-bind this device to a recovery code (another device's user id). The
  // docRef memo recomputes off userId, so the snapshot listener re-subscribes
  // to that account and its progress loads in. We show the loading state until
  // the first snapshot of the new doc arrives.
  const restoreProgress = useCallback((code) => {
    try {
      localStorage.setItem(USER_KEY, code);
    } catch {
      /* ignore quota / private-mode */
    }
    setLoaded(false);
    setUserId(code);
    setScreen("home");
  }, []);

  // ---- transient screen / lesson state ------------------------------------
  // home | lesson | done | games | game | stats | sync | settings | teach | learn
  const [screen, setScreen] = useState("home");
  const [activeGame, setActiveGame] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [lessonRun, setLessonRun] = useState(0); // bumps per attempt → fresh mount
  const [doneInfo, setDoneInfo] = useState(null);
  const [teach, setTeach] = useState(null); // { title, slides, cta, onDone, onClose }
  const [nodeChoice, setNodeChoice] = useState(null); // completed-node replay chooser (a built lesson)

  // Is a MIDI keyboard plugged in? Lessons bias toward *playing* when so.
  const midiConnected = useMidiPresence();

  const goHome = () => setScreen("home");

  // Open the teaching slides; `onDone` runs from the final slide's CTA.
  const openTeach = (cfg) => {
    setTeach({ cta: "got it", onClose: goHome, ...cfg });
    setScreen("teach");
  };

  const beginLesson = (lessonObj) => {
    setLesson(lessonObj);
    setLessonRun((n) => n + 1);
    setScreen("lesson");
  };

  // How lessons should be built right now: bias to playing under MIDI, run the
  // timed groove finale wherever audio works, and let mastery retire choice
  // questions for production-strength concepts.
  const lessonOpts = () => ({
    midi: midiConnected,
    timed: CAN_PLAY,
    mastery: progressRef.current.mastery || {},
  });

  const startLesson = (unit, idx) =>
    beginLesson({
      unit,
      idx,
      id: `${unit.id}-${idx}`,
      questions: buildLesson(unit, idx, lessonOpts()),
    });

  // A unit's "put it together" capstone — its own curriculum node, no teach
  // intro (it's a test of what the unit already taught).
  const startUnitCapstone = (unit) =>
    beginLesson({
      unit,
      idx: "cap",
      id: `${unit.id}-cap`,
      capstoneKind: "unitcap",
      questions: buildCapstone(unit, lessonOpts()),
    });

  // The track's final exam — a curriculum node after the last unit.
  const startTrackCapstone = () =>
    beginLesson({
      unit: null,
      idx: 0,
      id: `${activeTrack.id}-final`,
      capstoneKind: "trackcap",
      questions: buildTrackCapstone(activeTrack, lessonOpts()),
    });

  // Build (without starting) the lesson a path node maps to — so the replay
  // chooser can peek at a completed node's questions for a groove finale.
  const nodeLesson = (node) => {
    if (node.kind === "trackcap")
      return {
        unit: null,
        idx: 0,
        id: `${activeTrack.id}-final`,
        capstoneKind: "trackcap",
        questions: buildTrackCapstone(activeTrack, lessonOpts()),
      };
    const unit = activeTrack.units.find((u) => u.id === node.unitId);
    if (!unit) return null;
    if (node.kind === "unitcap")
      return {
        unit,
        idx: "cap",
        id: `${unit.id}-cap`,
        capstoneKind: "unitcap",
        questions: buildCapstone(unit, lessonOpts()),
      };
    return {
      unit,
      idx: node.idx,
      id: `${unit.id}-${node.idx}`,
      questions: buildLesson(unit, node.idx, lessonOpts()),
    };
  };

  // Tapping a path node. A completed node whose lesson ends in a timed groove
  // opens the replay chooser (from the top vs straight to the groove) instead
  // of silently relaunching the drill. Otherwise: a lesson's first node opens
  // its teaching intro first (then flows into the drill); later lessons,
  // retries, and the capstones go straight in.
  const onStartNode = (node) => {
    if ((progress.completed || {})[node.id]) {
      const built = nodeLesson(node);
      if (built && grooveIndex(built.questions) >= 0) {
        setNodeChoice(built);
        return;
      }
    }
    if (node.kind === "trackcap") return startTrackCapstone();
    const unit = activeTrack.units.find((u) => u.id === node.unitId);
    if (!unit) return;
    if (node.kind === "unitcap") return startUnitCapstone(unit);
    const i = node.idx;
    const slides = lessonTeach(unit, i);
    if (slides && slides.length) {
      openTeach({
        title: unit.title,
        slides,
        cta: "let's try it",
        onDone: () => startLesson(unit, i),
      });
    } else {
      startLesson(unit, i);
    }
  };

  // A spaced-repetition review weighted toward the learner's weakest concepts.
  // It awards XP + streak + mastery like a lesson but never marks a curriculum
  // node complete.
  const startReviewLesson = () => {
    const questions = buildReviewLesson(gameData.pairs, progress.mastery || {}, 8, {
      ...lessonOpts(),
      today: todayKey(), // staleness counts: decayed boxes review as weak
    });
    if (!questions.length) return;
    beginLesson({ unit: null, idx: 0, id: "review", review: true, questions });
  };

  // A targeted drill for one key — its scale + diatonic chords. Like review, it
  // awards XP + mastery but marks no curriculum node (review: true).
  const startKeyDrill = (key) =>
    beginLesson({
      unit: null,
      idx: 0,
      id: `drill-${key}`,
      review: true,
      questions: buildKeyDrill(key),
    });

  // The once-a-day challenge: a deterministic mixed set for the date, drawn
  // only from what this learner has unlocked (unlockAll lifts the filter).
  // Like review it marks no curriculum node, but it does record the day it
  // was completed (so home can show it's done) and counts toward the streak.
  const startDailyChallenge = () =>
    beginLesson({
      unit: null,
      idx: 0,
      id: "daily",
      daily: true,
      questions: buildDailyChallenge(todayKey(), {
        ...lessonOpts(),
        unlocked: progress.unlockAll ? null : gameData.unlocked,
        songs: progress.unlockAll ? null : gameData.songs,
      }),
    });

  // Relaunch the just-finished lesson from its timed groove finale — same
  // lesson identity (so passing still completes the node), questions sliced
  // to start at the groove. The drills before it were already played this
  // run; slipping on the finale shouldn't send anyone back to square one.
  const startFromGroove = () => {
    const gi = grooveIndex(lesson.questions);
    if (gi < 0) return;
    beginLesson({ ...lesson, questions: lesson.questions.slice(gi) });
  };

  // The lesson reports its outcome here; the page owns persistence. Mastery
  // updates regardless of pass/fail — missed concepts are exactly the ones
  // spaced repetition should resurface.
  const finishLesson = (passed, runtime) => {
    const { hearts, correct, total, results = [] } = runtime;
    // "back to the groove": a pass can replay the finale for fun; a fail can
    // pick up from it — but only if the run actually reached it.
    const gi = grooveIndex(lesson.questions);
    const hasGroove = gi >= 0;
    const reachedGroove = hasGroove && runtime.qi != null && runtime.qi >= gi;
    const mastery = applyResults(
      progressRef.current.mastery || {},
      results,
      todayKey()
    );
    // Timed play-the-changes bars carry a fast flag + landing latency — roll
    // them up so the done screen can show how in-time the playing was.
    const bars = results.filter((r) => r.type === "playprog");
    const lats = bars.filter((r) => r.ms != null);
    const timing = bars.length
      ? {
          onTimePct: Math.round(
            (100 * bars.filter((r) => r.fast).length) / bars.length
          ),
          avgMs: lats.length
            ? Math.round(lats.reduce((a, r) => a + r.ms, 0) / lats.length)
            : null,
        }
      : null;
    if (passed) {
      const award = lessonXp(hearts);
      const acc = total ? Math.round((correct / total) * 100) : 100;
      const today = todayKey();
      const streak = nextStreak(
        progressRef.current.streak,
        progressRef.current.lastDate,
        today
      );
      // Review and daily sessions never mark a curriculum node complete; the
      // daily additionally records the day it was finished.
      const isCurriculum = !lesson.review && !lesson.daily;
      persist({
        xp: (progressRef.current.xp || 0) + award,
        ...(isCurriculum
          ? { completed: { ...progressRef.current.completed, [lesson.id]: true } }
          : {}),
        ...(lesson.daily ? { dailyDate: today } : {}),
        streak,
        lastDate: today,
        mastery,
        history: recordHistory(progressRef.current.history || {}, today, {
          xp: award,
          lessons: lesson.review ? 0 : 1,
        }),
      });
      setDoneInfo({ passed: true, award, acc, hearts, timing, hasGroove });
    } else {
      persist({ mastery });
      setDoneInfo({ passed: false, correct, total, reachedGroove });
    }
    setScreen("done");
  };

  // ---- derived: unlocked material for the practice games ------------------
  const gameData = useMemo(() => {
    const unlocked = progress.unlockAll
      ? allConcepts()
      : unlockedConcepts(progress.completed);
    const pairs = unlocked.map((p) => {
      const [key, deg] = p.split(":");
      return { key, deg };
    });
    return {
      unlocked,
      pairs,
      keys: unlockedKeys(unlocked),
      earKeys: earReadyKeys(unlocked),
      songs: unlockedSongs(unlocked),
    };
  }, [progress.completed, progress.unlockAll]);

  // ---- derived: the selected track + its home path ------------------------
  const activeTrackId = progress.track || "major";
  const activeTrack = TRACKS.find((t) => t.id === activeTrackId) || TRACKS[0];
  const selectTrack = (id) => persist({ track: id });

  const homeUnits = useMemo(() => {
    const completed = progress.completed || {};
    const units = activeTrack.units;
    const mastery = progress.mastery || {};
    const today = todayKey();
    const unitById = Object.fromEntries(units.map((u) => [u.id, u]));
    // The path is a strict linear chain (lessons → unit capstone → … → track
    // capstone), so a node unlocks once the node before it is done — which is
    // what makes the capstones required to advance.
    let firstIncompleteFound = false;
    let prevId = null;
    const annotated = trackPath(activeTrack).map((node) => {
      const done = !!completed[node.id];
      const unlocked =
        progress.unlockAll || done || prevId === null || !!completed[prevId];
      const cap = node.kind !== "lesson";
      let cls = `node${cap ? " cap" : ""}`;
      let icon = cap ? "trophy" : "note";
      // A completed chords lesson goes "due" once its concepts' Leitner boxes
      // outlive their freshness window — tapping it re-runs the lesson, so
      // review pressure lives on the path, not just behind the menu.
      const due =
        done &&
        node.kind === "lesson" &&
        conceptsDue(
          lessonConceptPairs(unitById[node.unitId], node.idx),
          mastery,
          today
        ).length > 0;
      // The label always names the lesson (scale / major / minor / changes, or a
      // capstone) so the per-key structure stays visible; state is shown by the
      // icon + colour (check = done, lock = locked, trophy/pulse = capstone/next).
      const label =
        node.kind === "unitcap"
          ? "put it together"
          : node.kind === "trackcap"
          ? "final test"
          : node.lessonLabel
          ? node.lessonLabel
          : node.idx === 0
          ? "start"
          : `lesson ${node.idx + 1}`;
      if (done) {
        cls += " done";
        if (due) cls += " due";
        icon = "check";
      } else if (unlocked && !firstIncompleteFound) {
        cls += " current pulse";
        firstIncompleteFound = true;
      } else if (unlocked) {
        cls += " current";
      } else {
        cls += " locked";
        icon = "lock";
      }
      prevId = node.id;
      return { ...node, done, due, unlocked, cls, icon, label };
    });

    // Group for rendering: each unit (its lessons + its capstone), then a final
    // group for the track exam.
    const groups = units.map((u, ui) => ({
      unit: u,
      ui,
      kind: "unit",
      teachable: !!unitTeach(u.id),
      nodes: annotated.filter((n) => n.unitId === u.id),
    }));
    const finalNode = annotated.find((n) => n.kind === "trackcap");
    if (finalNode) {
      groups.push({
        unit: { id: finalNode.id, title: "the final test", gloss: "put it all together" },
        ui: units.length,
        kind: "final",
        teachable: false,
        nodes: [finalNode],
      });
    }
    return groups;
  }, [progress.completed, progress.unlockAll, progress.mastery, activeTrack]);

  // How many drilled concepts have gone stale — the home screen's review nudge.
  const reviewDue = useMemo(
    () => conceptsDue(gameData.pairs, progress.mastery || {}, todayKey()).length,
    [gameData.pairs, progress.mastery]
  );

  // Path nodes completed within the active track (for the progress line) —
  // counts the capstone nodes too, since they're part of the path now.
  const trackNodes = trackPath(activeTrack);
  const totalLessons = trackNodes.length;
  const doneLessons = trackNodes.filter(
    (n) => progress.completed && progress.completed[n.id]
  ).length;

  // ---- render --------------------------------------------------------------
  if (!loaded) {
    return (
      <div className="ns-root">
        <div className="ns-app">
          <div className="done-screen">
            <Keys />
            <div className="sub" style={{ marginTop: 16 }}>
              tuning up… (loading your progress)
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "home") {
    return (
      <>
      <HomeScreen
        progress={progress}
        homeUnits={homeUnits}
        gameData={gameData}
        doneLessons={doneLessons}
        totalLessons={totalLessons}
        tracks={TRACKS}
        activeTrackId={activeTrackId}
        onSelectTrack={selectTrack}
        dailyDone={dailyDoneToday(progress.dailyDate, todayKey())}
        dailyGoal={dailyGoalProgress(progress.history, todayKey())}
        reviewDue={reviewDue}
        onDaily={startDailyChallenge}
        onStartNode={onStartNode}
        onLearnUnit={(ui) => {
          const unit = activeTrack.units[ui];
          const slides = unitTeach(unit.id);
          if (slides) openTeach({ title: unit.title, slides });
        }}
        onOpenMenu={() => setScreen("menu")}
        onReview={startReviewLesson}
        onSaveName={(name) => persist({ name })}
      />
      {nodeChoice && (
        <GrooveChoice
          label={
            nodeChoice.capstoneKind === "trackcap"
              ? "the final"
              : nodeChoice.idx === "cap"
              ? `the ${nodeChoice.unit.title} capstone`
              : `${nodeChoice.unit.title}, lesson ${nodeChoice.idx + 1}`
          }
          onClose={() => setNodeChoice(null)}
          onFull={() => {
            const built = nodeChoice;
            setNodeChoice(null);
            beginLesson(built);
          }}
          onGroove={() => {
            const built = nodeChoice;
            setNodeChoice(null);
            beginLesson({
              ...built,
              questions: built.questions.slice(grooveIndex(built.questions)),
            });
          }}
        />
      )}
      </>
    );
  }

  if (screen === "menu") {
    return (
      <MenuScreen
        gameData={gameData}
        onOpenGames={() => setScreen("games")}
        onReview={startReviewLesson}
        onOpenDrill={() => setScreen("drill")}
        onOpenFlow={() => setScreen("flow")}
        onOpenJam={() => setScreen("jam")}
        onOpenTrainer={() => setScreen("trainer")}
        onOpenChart={() => setScreen("chart")}
        onOpenLearn={() => setScreen("learn")}
        onOpenStats={() => setScreen("stats")}
        onOpenSync={() => setScreen("sync")}
        onOpenSettings={() => setScreen("settings")}
        onBack={goHome}
      />
    );
  }

  if (screen === "teach" && teach) {
    return (
      <AudioSettingsContext.Provider
        value={{ tempo: settings.tempo, countIn: false }}
      >
        <TeachScreen
          title={teach.title}
          slides={teach.slides}
          cta={teach.cta}
          onDone={teach.onDone}
          onClose={teach.onClose}
        />
      </AudioSettingsContext.Provider>
    );
  }

  if (screen === "learn") {
    return (
      <LearnScreen
        onBack={goHome}
        onOpen={({ title, slides }) =>
          openTeach({ title, slides, onClose: () => setScreen("learn"), onDone: () => setScreen("learn") })
        }
      />
    );
  }

  if (screen === "drill") {
    return (
      <DrillScreen
        gameData={gameData}
        onDrillKey={startKeyDrill}
        onReview={startReviewLesson}
        onBack={() => setScreen("menu")}
      />
    );
  }

  if (screen === "flow") {
    return (
      <FlowScreen unlockedKeys={gameData.keys} onBack={() => setScreen("menu")} />
    );
  }

  if (screen === "jam") {
    return <JamScreen settings={settings} onBack={goHome} />;
  }

  if (screen === "trainer") {
    return <PatternTrainerScreen onBack={goHome} />;
  }

  if (screen === "chart") {
    // Chart mode: the band-simulation payoff. It grades like the games —
    // results feed mastery in one batch, a passed run awards XP.
    return (
      <AudioSettingsContext.Provider
        value={{ tempo: settings.tempo, countIn: settings.countIn }}
      >
        <ChartScreen
          unlocked={progress.unlockAll ? null : gameData.unlocked}
          onBack={() => setScreen("menu")}
          onAward={awardXp}
          onResults={recordResults}
        />
      </AudioSettingsContext.Provider>
    );
  }

  if (screen === "settings") {
    return (
      <SettingsScreen
        settings={settings}
        onChange={updateSettings}
        onBack={goHome}
        unlocked={Boolean(progress.unlockAll)}
        onUnlockAll={() => persist({ unlockAll: true })}
      />
    );
  }

  if (screen === "stats") {
    return (
      <StatsScreen progress={progress} doneLessons={doneLessons} onBack={goHome} />
    );
  }

  if (screen === "sync") {
    return (
      <SyncScreen
        code={userId}
        isCloud={!!docRef}
        onBack={goHome}
        onRestore={restoreProgress}
      />
    );
  }

  if (screen === "games") {
    return (
      <GamesHub
        xp={progress.xp}
        gameData={gameData}
        onBack={goHome}
        onPlay={(key) => {
          setActiveGame(key);
          setScreen("game");
        }}
      />
    );
  }

  if (screen === "game") {
    return (
      <AudioSettingsContext.Provider
        value={{ tempo: settings.tempo, countIn: settings.countIn }}
      >
        <GameScreen
          activeGame={activeGame}
          gameData={gameData}
          xp={progress.xp}
          onBack={() => {
            setActiveGame(null);
            setScreen("games");
          }}
          onAward={awardXp}
          onResults={recordResults}
        />
      </AudioSettingsContext.Provider>
    );
  }

  if (screen === "done" && doneInfo) {
    return (
      <DoneScreen
        info={doneInfo}
        onHome={goHome}
        onGroove={startFromGroove}
        onRetry={() =>
          lesson.daily
            ? startDailyChallenge()
            : lesson.review
            ? startReviewLesson()
            : lesson.capstoneKind === "unitcap"
            ? startUnitCapstone(lesson.unit)
            : lesson.capstoneKind === "trackcap"
            ? startTrackCapstone()
            : startLesson(lesson.unit, lesson.idx)
        }
      />
    );
  }

  if (screen === "lesson" && lesson) {
    return (
      <AudioSettingsContext.Provider
        value={{ tempo: settings.tempo, countIn: settings.countIn }}
      >
        <LessonScreen
          key={lessonRun}
          lesson={lesson}
          onClose={goHome}
          onFinish={finishLesson}
        />
      </AudioSettingsContext.Provider>
    );
  }

  return null;
};

export default NumberSense;
